import React from "react";
import { css } from "@emotion/css";
import { Text, Separator } from "components";
import { CloseIcon } from "components/icons";
import { T_Todo } from "types";

interface TodoDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  task: T_Todo | null;
}

const fakeError = {
  status: 503,
  error: {
    errorId: "d7f8e2b3-40d9-4cfc-9d95-2f0b3d5c9f28",
    errorType: "NoResourcesAvailable",
    errorTitle: "No Available Resources",
    errorMessage:
      "The resource(s) you requested are currently not available. Please try again later.",
    errorDetails: {
      description:
        "This error occurs when the requested resources cannot be found on the server. It may be caused by scheduled maintenance, high server load, or temporary data unavailability. Our system is designed to handle peak usage periods and maintain data integrity, but occasionally, resources may be taken offline momentarily to ensure overall service reliability.",
      possibleCauses: [
        "Server undergoing routine maintenance or unexpected issues",
        "High traffic causing temporary resource unavailability",
        "Data synchronization delays between services",
        "Configuration updates resulting in momentary downtime",
      ],
      suggestedActions: {
        user: "Please wait a few minutes before retrying your request. Ensure that your request parameters are correct and that you are accessing the proper endpoint.",
        developer:
          "Check the current server logs for detailed error messages, review system monitoring metrics, and verify that all dependent services are operating normally. If the issue persists, please refer to the documentation or contact support for further assistance.",
      },
      technicalInfo: {
        httpStatusCode: 503,
        retryAfterSeconds: 120,
        apiEndpoint: "/v1/resources",
        server: "Gemini API Server 01",
        timestamp: "2025-04-11T12:34:56.789Z",
      },
    },
  },
  request: {
    method: "GET",
    url: "/v1/resources",
    parameters: {},
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  },
  metadata: {
    documentationUrl: "https://ai.google.dev/gemini-api/docs/troubleshooting",
    contact: {},
    traceId: "b2a5f63e-8d27-41f2-9e01-0e8e5c312a90",
  },
};

export const TodoDrawer: React.FC<TodoDrawerProps> = ({
  isOpen,
  onClose,
  task,
}) => {
  if (!isOpen || !task) {
    return null;
  }

  return (
    <div
      className={css`
        position: fixed;
        top: 0;
        right: 0;
        width: 400px; // Adjust width as needed
        height: 100%;
        background-color: #181823; // Example background color
        border-left: 1px solid #272539;
        box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
        padding: 20px;
        z-index: 1000;
        display: flex;
        flex-direction: column;
        transition: transform 0.3s ease-in-out;
        transform: ${isOpen ? "translateX(0)" : "translateX(100%)"};
      `}
    >
      <div
        className={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        `}
      >
        <Text.H4>Task</Text.H4>
        <button
          onClick={onClose}
          className={css`
            background: none;
            border: none;
            cursor: pointer;
            padding: 0;
          `}
          aria-label="Close drawer"
        >
          <CloseIcon fill="highlight/1" />
        </button>
      </div>
      <Separator.Horizontal />
      <div
        className={css`
          flex-grow: 1;
          overflow-y: auto;
          margin-top: 20px;
        `}
      >
        <Text.Body>Title: {task.title}</Text.Body>
        <Text.Body>
          Due Date:{" "}
          {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : "N/A"}
        </Text.Body>
        {/* Add more task details here in the future */}
        <Text.Body style={{ marginTop: "20px", fontStyle: "italic" }}>
          {JSON.stringify(fakeError, null, 2)}
        </Text.Body>
      </div>
    </div>
  );
};
